import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { parseBannerList } from 'helpers/parseBannerList';

import { Slider } from '@atlas/components';
import {
  AccountManager,
  btag,
  pushToDataLayer,
  useApplicationState,
  useBannerGroupList,
  useCasinoNewState,
} from '@atlas/core';

import { filterBannersList } from 'common/utils';
import { useIsRtl } from 'hooks/useIsRtl';
import { GROUPS } from 'src/components/Casino/constants';

export const CasinoBanner = () => {
  const { authenticated } = useApplicationState();
  const { group } = useCasinoNewState();
  const { bannersChunk } = useBannerGroupList(
    'casino',
    'desktop',
    group === 'LIVE' && 'live_desktop',
  );
  const isRtl = useIsRtl();

  const groups = useMemo(() => bannersChunk.map(parseBannerList), [bannersChunk]);
  const dir = useMemo(() => (isRtl ? 'rtl' : 'ltr'), [isRtl]);

  const filteredGroups = useMemo(
    () =>
      groups
        .map(group => filterBannersList(group, authenticated))
        .filter(group => group.length > 0),
    [authenticated, groups],
  );

  const handleClick = useCallback(
    imageUrl => {
      const { clientId } = AccountManager.account;

      pushToDataLayer(
        group === GROUPS.CASINO ? 'сasino_banner_click' : 'live_сasino_banner_click',
        { client: { id: clientId } },
        {
          image_URL: imageUrl,
        },
        btag,
      );
    },
    [group],
  );

  if (!filteredGroups.length) return null;

  return (
    <Slider
      autoplay={{
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }}
      className={cn('slider', { 'slider--single-slide': filteredGroups.length === 1 })}
      dir={dir}
      isNewDesign
      loop={filteredGroups.length > 1}
      nextBtn={<Slider.NextBtn />}
      prevBtn={<Slider.PrevBtn />}
      slidesPerView={1}
    >
      {filteredGroups.map(group => (
        <Slider.Slide key={group.reduce((acc, next) => `${acc}${next.imageUrl}`, '')}>
          <Slider.Group>
            {group.map(({ href, imageUrl, target, title }) => (
              <Slider.Banner
                href={href}
                imageUrl={imageUrl}
                key={imageUrl}
                onClick={() => handleClick(imageUrl)}
                target={target}
                title={title}
              />
            ))}
          </Slider.Group>
        </Slider.Slide>
      ))}
    </Slider>
  );
};
